"use client";

import { useState } from "react";
import { LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";

interface LogoutButtonProps {
  className?: string;
  variant?: "button" | "icon";
}

const LogoutButton = ({ className = "", variant = "button" }: LogoutButtonProps) => {
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslations();

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      // Logout function handles both backend and frontend logout, plus redirect
      await logout();
    } catch (e) {
      console.error("Logout failed", e);
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === "icon") {
    return (
      <Button
        size="icon"
        onClick={handleLogout}
        disabled={isLoading}
        title="Sign out"
        className={cn("rounded-lg border bg-background text-muted-foreground shadow-none hover:border-red-200 hover:bg-red-50 hover:text-red-600", className)}
      >
        <LogOut size={16} />
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      onClick={handleLogout}
      disabled={isLoading}
      className={cn("space-x-2 rounded-lg px-3 py-2 text-red-600 hover:bg-red-50 hover:text-red-700", className)}
    >
      <LogOut size={16} />
      <span>{isLoading ? t("authentication.signingOut") : t("authentication.signOut")}</span>
    </Button>
  );
};

export default LogoutButton;
